/**
 * Wave 4 stage K (analytics): the player's analytics choice.
 *
 *   null     not asked yet (the menu shows the question once analytics are on)
 *   granted  "yes": a random anonymous player id is created and kept in this browser
 *   denied   "no": nothing is stored; only anonymous session counts are sent
 *
 * Withdrawing (granted → denied) removes the id here and asks the server to delete its rows.
 */
import { signal } from "@preact/signals";
import { ANON_ID_RE } from "../../shared/analytics";
import { readString, removeKey, writeString } from "../storage/local";

export type ConsentChoice = "granted" | "denied";

const CONSENT_KEY = "analytics.consent";
const PID_KEY = "analytics.pid";

function readChoice(): ConsentChoice | null {
  const v = readString(CONSENT_KEY);
  return v === "granted" || v === "denied" ? v : null;
}

/** The current choice (null = undecided). */
export const consent = signal<ConsentChoice | null>(readChoice());

/** A fresh random anonymous id (32 hex characters; no link to the person or the device). */
export function newAnonId(): string {
  const bytes = new Uint8Array(16);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Re-read the stored choice (another tab may have changed it). A withdrawal there also drops this
 * tab's copy of the id (the in-memory storage fallback keeps one).
 */
export function syncConsent(): void {
  const stored = readChoice();
  if (stored === "denied") removeKey(PID_KEY);
  if (stored !== null && consent.value !== stored) consent.value = stored;
}

/** The anonymous player id: only with consent (created on first use), otherwise null. */
export function anonId(): string | null {
  syncConsent();
  if (consent.value !== "granted") return null;
  const stored = readString(PID_KEY);
  if (stored && ANON_ID_RE.test(stored)) return stored;
  const id = newAnonId();
  writeString(PID_KEY, id);
  return id;
}

/**
 * Record the player's choice. Returns the id that was dropped when consent was withdrawn (the
 * caller asks the server to forget it), else null.
 */
export function setConsent(choice: ConsentChoice): string | null {
  const previous = readString(PID_KEY);
  writeString(CONSENT_KEY, choice);
  consent.value = choice;
  if (choice === "denied") {
    removeKey(PID_KEY);
    return previous && ANON_ID_RE.test(previous) ? previous : null;
  }
  return null;
}

/** Tests only: forget everything. */
export function resetConsentForTests(): void {
  removeKey(CONSENT_KEY);
  removeKey(PID_KEY);
  consent.value = null;
}
